import { CampaignType, Platform, getApexInfluencers, getInstagramUserImages, getTiktokUserImages } from '@round/api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ClientBookingContext, ClientBookingDispatchContext } from './ClientBookingContext/ClientBookingContext';
import useAbortableEffect from '../../../hooks/useAbortableEffect';
import useNonNullContext from '../../../hooks/useNonNullContext';
import { ClientInfluencerUserImage } from './ClientBookingContext/reducer';
import { getSelectedPlatforms } from './containers/BookingDetailsDropdown/helpers';
import uniq from 'lodash/uniq';

export type BookingCartItem = {
    influencerId: number | null;
    name: string | null;
    platform: Platform;
    cost: number | null;
    offer: number | null;
    followers: number | null;
};

export type PriceOffer = { influencerId: number; platform: Platform; price: number | null };

export function useClientBookingData(
    bookedInfluencerIds: { tiktok: number[]; instagram: number[] },
    selectedCurrency: number | undefined,
    campaignType: CampaignType
) {
    const { influencers, influencerImages, currency, influencersInitialized, imagesInitialized } =
        useNonNullContext(ClientBookingContext);

    const [priceOffers, setPriceOffers] = useState<PriceOffer[]>([]);

    const handleSetPriceOffer = (newOffer: PriceOffer) => {
        const existingOfferIndex = priceOffers.findIndex(
            (offer) => offer.influencerId === newOffer.influencerId && offer.platform === newOffer.platform
        );
        if (existingOfferIndex >= 0) {
            setPriceOffers((prev) => {
                prev.splice(existingOfferIndex, 1);
                return prev.concat(newOffer);
            });
        } else {
            setPriceOffers((prev) => prev.concat(newOffer));
        }
    };

    const dispatch = useNonNullContext(ClientBookingDispatchContext);
    const influencerIds = Array.from(new Set(bookedInfluencerIds.tiktok.concat(bookedInfluencerIds.instagram)));

    const fetchInfluencers = useCallback(
        async (requestInit: RequestInit) => {
            if (!selectedCurrency || (influencersInitialized && currency === selectedCurrency)) {
                return;
            }
            if (!influencerIds.length) {
                dispatch({
                    type: 'initializeInfluencers',
                    payload: { influencers: [], currency: selectedCurrency },
                });
                return;
            }
            const fetchedInfluencers = await getApexInfluencers(
                { id: influencerIds.join(), currency: selectedCurrency },
                requestInit
            ).then((response) => (response.status === 200 ? response.data.results : []));
            dispatch({
                type: 'initializeInfluencers',
                payload: {
                    influencers: fetchedInfluencers,
                    currency: selectedCurrency,
                },
            });
        },
        [influencerIds, influencersInitialized, currency, selectedCurrency, dispatch]
    );

    useAbortableEffect(
        (signal) => {
            fetchInfluencers({ signal });
        },
        [fetchInfluencers]
    );

    const fetchImages = useCallback(
        async (requestInit: RequestInit) => {
            if (imagesInitialized) {
                return;
            }
            if (!influencers.length) {
                dispatch({
                    type: 'initializeImages',
                    payload: { influencerImages: [] },
                });
            }
            const influencerTiktokIds = influencers
                .map((inf) => inf.tiktok_user_id)
                .filter((id): id is number => typeof id === 'number');
            const influencerInstagramIds = influencers
                .map((inf) => inf.instagram_user_id)
                .filter((id): id is number => typeof id === 'number');

            const fetchTiktokUserImages = async (ids: number[]) => {
                if (!ids.length) {
                    return [];
                }
                return getTiktokUserImages(ids, requestInit);
            };
            const fetchInstagramImages = async (ids: number[]) => {
                if (!ids.length) {
                    return [];
                }
                return (await getInstagramUserImages(ids)).data;
            };

            const [tiktokImages, instagramImages] = await Promise.all([
                fetchTiktokUserImages(influencerTiktokIds),
                fetchInstagramImages(influencerInstagramIds),
            ]);

            const mappedInfluencerImages: ClientInfluencerUserImage[] = influencers.flatMap((inf) => {
                const image =
                    tiktokImages.find((img) => img.user_id === inf.tiktok_user_id) ||
                    instagramImages.find((img) => img.user_id === inf.instagram_user_id);
                if (!image) {
                    return [];
                }
                return [
                    {
                        influencerId: inf.id,
                        image: image,
                    },
                ];
            });

            dispatch({
                type: 'initializeImages',
                payload: {
                    influencerImages: mappedInfluencerImages,
                },
            });
        },
        [influencers, dispatch, imagesInitialized]
    );

    useAbortableEffect(
        (signal) => {
            fetchImages({ signal });
        },
        [fetchImages]
    );

    const bookingCartItems = useMemo(() => {
        const tiktokBookings: BookingCartItem[] = bookedInfluencerIds.tiktok.map((id) => {
            const influencer = influencers.find((inf) => inf.id === id);
            const cost =
                campaignType === 'AUDIO'
                    ? influencer?.cost_tiktok_audio ?? null
                    : influencer?.cost_tiktok_brand ?? null;
            const offer = priceOffers.find((offer) => offer.influencerId === id && offer.platform === 'TIKTOK')?.price;

            return {
                influencerId: influencer?.id ?? null,
                name: influencer?.name ?? null,
                cost,
                offer: offer ?? null,
                followers: influencer?.tiktok_follower_count ?? null,
                platform: 'TIKTOK',
            };
        });
        const instagramBookings: BookingCartItem[] = bookedInfluencerIds.instagram.map((id) => {
            const influencer = influencers.find((inf) => inf.id === id);
            const cost =
                campaignType === 'AUDIO'
                    ? influencer?.cost_instagram_audio ?? null
                    : influencer?.cost_instagram_brand ?? null;
            const offer = priceOffers.find((offer) => offer.influencerId === id && offer.platform === 'INSTAGRAM')
                ?.price;

            return {
                influencerId: influencer?.id ?? null,
                name: influencer?.name ?? null,
                cost,
                offer: offer ?? null,
                followers: influencer?.instagram_follower_count ?? null,
                platform: 'INSTAGRAM',
            };
        });
        return tiktokBookings.concat(instagramBookings);
    }, [campaignType, bookedInfluencerIds, influencers, priceOffers]);

    const selectedPlatforms = useMemo(
        () => getSelectedPlatforms(bookedInfluencerIds.tiktok, bookedInfluencerIds.instagram),
        [bookedInfluencerIds]
    );
    const totalCreators = uniq(bookingCartItems.map((item) => item.influencerId)).length;
    const totalCost = useMemo(
        () =>
            bookingCartItems.reduce((agg, item) => {
                const offer = priceOffers.find(
                    (offer) => offer.influencerId === item.influencerId && offer.platform === item.platform
                )?.price;
                if (offer) {
                    return agg + offer;
                }
                if (item.cost) {
                    return agg + item.cost;
                }
                return agg;
            }, 0),
        [bookingCartItems, priceOffers]
    );

    useEffect(() => {
        if (!influencers.length) {
            return;
        }
        const influencersToRemove = influencers
            .map((inf) => inf.id)
            .filter((id) => !bookedInfluencerIds.instagram.includes(id) && !bookedInfluencerIds.tiktok.includes(id));
        influencersToRemove.forEach((id) => dispatch({ type: 'removeInfluencerFromBooking', payload: id }));
    }, [dispatch, influencers, bookedInfluencerIds]);

    return {
        bookingCartItems,
        influencerImages,
        selectedPlatforms,
        totalCost,
        totalCreators,
        influencersInitialized,
        handleSetPriceOffer,
    };
}
